/**
 * Super-admin-only card to choose which LLM the Atlas real-coding agent uses
 * (Kimi k2.7-code / Kimi k3 / DeepSeek v4-pro). Returns null for everyone else,
 * so it auto-hides on the shared Settings page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type CSSProperties } from "react";

import { api } from "@/lib/api";
import type { AtlasConfig } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

export function AtlasModelCard() {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["atlas-config"],
    queryFn: async () => (await api.get<AtlasConfig>("/superadmin/atlas-config")).data,
    enabled: isSuperAdmin,
  });

  const update = useMutation({
    mutationFn: async (model: string) =>
      (await api.put<AtlasConfig>("/superadmin/atlas-config", { model })).data,
    onSuccess: (next) => queryClient.setQueryData(["atlas-config"], next),
  });

  if (!isSuperAdmin) return null;

  return (
    <section style={card}>
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>Atlas coding model</h2>
      <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
        The model the autonomous coding agent uses to implement backlog tickets. Applies to the next
        runs across every project. The selected provider's API key must be configured on the workers.
      </p>

      {isLoading || !data ? (
        <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading…</p>
      ) : (
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--mute)" }}>
          Model
          <select
            value={data.current ?? ""}
            onChange={(e) => update.mutate(e.target.value)}
            disabled={update.isPending}
            style={select}
          >
            {data.current === null && <option value="">Default (from environment)</option>}
            {data.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {update.isError && (
        <p style={{ color: "var(--danger, #e5484d)", fontSize: 12, marginTop: 10 }}>
          Could not save. Try again.
        </p>
      )}
    </section>
  );
}

const card: CSSProperties = {
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  background: "var(--canvas)",
  padding: 20,
  marginBottom: 20,
};

const select: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontSize: 13,
  color: "var(--ink)",
  padding: "7px 10px",
  minWidth: 260,
  cursor: "pointer",
};
